/**
 * Predefined seasonal campaign theme styles used by the public
 * "Catálogo Especial de Temporada" section.
 * Each style object maps a theme id to its visual tokens.
 */
export interface CampaignThemeStyles {
  bg: string;
  textColor: string;
  subColor: string;
  labelColor: string;
  accentBg: string;
}

const DEFAULT_STYLES: CampaignThemeStyles = {
  bg: "linear-gradient(135deg, #fdf2f8 0%, #fbcfe8 100%)", // pink-50 to pink-200
  textColor: "text-pink-900",
  subColor: "text-pink-750/75",
  labelColor: "text-pink-600",
  accentBg: "bg-pink-100",
};

const THEME_VARIANTS: Record<string, CampaignThemeStyles> = {
  padres: {
    bg: "linear-gradient(135deg, #f0f5ff 0%, #dbeafe 100%)", // blue-50 to blue-100
    textColor: "text-blue-900",
    subColor: "text-blue-750/75",
    labelColor: "text-blue-600",
    accentBg: "bg-blue-100",
  },
  mundial: {
    bg: "linear-gradient(135deg, #f0fdf4 0%, #bbf7d0 100%)", // emerald-50 to emerald-100
    textColor: "text-emerald-900",
    subColor: "text-emerald-750/75",
    labelColor: "text-emerald-600",
    accentBg: "bg-emerald-100",
  },
  valentina: {
    bg: "linear-gradient(135deg, #fff5f5 0%, #ffe4e6 100%)", // red-50 to rose-100
    textColor: "text-red-900",
    subColor: "text-red-750/75",
    labelColor: "text-red-600",
    accentBg: "bg-red-100",
  },
  navidad: {
    bg: "linear-gradient(135deg, #fff5f5 0%, #e8f5e9 100%)", // warm red-50 to emerald-50
    textColor: "text-red-950",
    subColor: "text-emerald-800/80",
    labelColor: "text-red-600",
    accentBg: "bg-red-100",
  },
  halloween: {
    bg: "linear-gradient(135deg, #fff7ed 0%, #ffedd5 50%, #fed7aa 100%)", // beautiful orange gradient
    textColor: "text-orange-950",
    subColor: "text-orange-850/80",
    labelColor: "text-orange-700",
    accentBg: "bg-orange-100",
  },
  dorado: {
    bg: "linear-gradient(135deg, #fffbeb 0%, #fef3c7 50%, #fde68a 100%)", // classy shimmering gold
    textColor: "text-amber-950",
    subColor: "text-amber-800/85",
    labelColor: "text-amber-700",
    accentBg: "bg-amber-100",
  },
};

/**
 * Returns the visual styles for a campaign theme id, falling back to
 * the default "madres" (pink) styles for unknown themes.
 */
export const getCampaignThemeStyles = (theme: string): CampaignThemeStyles =>
  THEME_VARIANTS[theme] || DEFAULT_STYLES;
